import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';

import { ErrorState, LoadingState } from '@/components/admin';
import { useClient } from '@/features/clients/hooks';
import { SessionNoteEditor } from '@/features/sessions/components/SessionNoteEditor';
import { useSession, useSessionNote } from '@/features/sessions/hooks';
import { requirePermission } from '@/lib/guards';
import { surfacePaths } from '@/lib/paths';
import { useAuthStore } from '@/stores/authStore';

export const Route = createFileRoute('/_staff/admin/workspace/sessions/$sessionId/note')({
  beforeLoad: () => {
    requirePermission('session_note:create');
  },
  component: SessionNoteScreen,
  // Editor page owns the full canvas — no shell padding around the card.
  staticData: { fullBleed: true },
});

export function SessionNoteScreen() {
  const { sessionId } = useParams({ from: '/_staff/admin/workspace/sessions/$sessionId/note' });
  const navigate = useNavigate();
  const sessionQ = useSession(sessionId);
  const noteQuery = useSessionNote(sessionId);
  const clientQ = useClient(sessionQ.data?.clientId ?? undefined);
  const userFirstName = useAuthStore((s) => s.user?.firstName ?? 'you');
  const paths = surfacePaths('workspace');

  if (sessionQ.isLoading || noteQuery.isLoading) return <LoadingState variant="form" />;
  if (sessionQ.error || noteQuery.error) {
    const err = noteQuery.error ?? sessionQ.error;
    return (
      <ErrorState
        description={err instanceof Error ? err.message : undefined}
        onRetry={() => {
          void noteQuery.refetch();
          void sessionQ.refetch();
        }}
      />
    );
  }
  if (sessionQ.data === undefined) return <p className="p-6">Session not found.</p>;

  const session = sessionQ.data;
  const client = clientQ.data;
  const clientName =
    client !== undefined
      ? `${client.firstName} ${client.lastName}`
      : (session.clientName ?? 'Client');

  const returnToSession = (): void => {
    void navigate({ to: paths.sessions.detail(sessionId) });
  };

  // Mirror the clinical-surface route — full-bleed editor, no JournalPageHeader
  // / StatusTimeline wrapper. The new ClinicalEditorShell carries its own chrome.
  return (
    <SessionNoteEditor
      sessionId={sessionId}
      practitionerId={session.practitionerId}
      clientId={session.clientId}
      clientName={clientName}
      scheduledStart={session.scheduledStart}
      authorName={userFirstName}
      note={noteQuery.data ?? null}
      onSubmitted={returnToSession}
    />
  );
}
